/**
 * Inactivity timeout: after 1 hour without input, clear session (tokens + profile).
 * Activity also schedules a debounced proactive token refresh (keeps active users signed in).
 */

import { useEffect, useRef } from 'react';
import { ensureAccessTokenFresh } from './customerSession';

export const CUSTOMER_LAST_ACTIVITY_KEY = 'coremonit_customer_last_activity_at';

/** Default 1 hour — industry default for sensitive apps; override via env if needed later. */
export const IDLE_LOGOUT_AFTER_MS = 60 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'] as const;

export function touchSessionActivity(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(CUSTOMER_LAST_ACTIVITY_KEY, String(Date.now()));
  } catch {
    // ignore quota / private mode
  }
}

export function clearSessionActivityTimestamp(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(CUSTOMER_LAST_ACTIVITY_KEY);
}

/**
 * When `enabled`, track last activity and force logout after `idleMs` of silence.
 * Debounced proactive JWT refresh ~60s after activity bursts.
 */
export function useCustomerSessionIdle(
  enabled: boolean,
  onIdleLogout: () => void,
  idleMs: number = IDLE_LOGOUT_AFTER_MS
): void {
  const logoutRef = useRef(onIdleLogout);
  logoutRef.current = onIdleLogout;

  useEffect(() => {
    if (!enabled) return;

    touchSessionActivity();

    let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleProactiveRefresh = () => {
      if (proactiveTimer) clearTimeout(proactiveTimer);
      proactiveTimer = setTimeout(() => {
        proactiveTimer = null;
        void ensureAccessTokenFresh().catch(() => undefined);
      }, 60_000);
    };

    const onActivity = () => {
      touchSessionActivity();
      scheduleProactiveRefresh();
    };

    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, onActivity as EventListener, { passive: true });
    });

    const interval = window.setInterval(() => {
      const raw = localStorage.getItem(CUSTOMER_LAST_ACTIVITY_KEY);
      if (!raw) return;
      const last = parseInt(raw, 10);
      if (Number.isNaN(last)) return;
      if (Date.now() - last > idleMs) {
        console.info('[customer-session] idle timeout, signing out', {
          idle_ms: idleMs,
          last_activity: new Date(last).toISOString(),
        });
        clearSessionActivityTimestamp();
        logoutRef.current();
      }
    }, 60_000);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity as EventListener));
      window.clearInterval(interval);
      if (proactiveTimer) clearTimeout(proactiveTimer);
    };
  }, [enabled, idleMs]);
}
